import { useEffect, useState } from "react";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  scoreDetailQuery,
  type ScoreAudit,
  type ScoreDetail,
} from "@/lib/queries";
import { useApplyAudit } from "@/lib/mutations";
import { useGameEvents } from "@/lib/sse";
import { createYoloWorker } from "@/lib/yolo";
import { Button, buttonStyles } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/games/$id/scores/$scoreId/audit")({
  component: ScoreAuditPage,
  // No auth guard: anyone in the game (per-cookie) can view + audit.
  // The audit POST handler validates the player cookie server-side.
  loader: async ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      scoreDetailQuery(params.id, params.scoreId),
    );
  },
});

function ScoreAuditPage() {
  const { id, scoreId } = Route.useParams();

  useGameEvents(id);

  const detailQ = useQuery(scoreDetailQuery(id, scoreId));
  const detail = detailQ.data;

  const audit = useApplyAudit(id, detail?.player.id ?? "", scoreId);

  const [total, setTotal] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // When the score detail loads (or refreshes), prefill the input with the
  // current total so the form starts on the existing value.
  useEffect(() => {
    if (detail) setTotal(String(detail.score.total));
  }, [detail?.score.total]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear the "Saved" banner after 1.5s.
  useEffect(() => {
    if (savedAt === null) return;
    const t = window.setTimeout(() => setSavedAt(null), 1500);
    return () => window.clearTimeout(t);
  }, [savedAt]);

  if (detailQ.error) {
    const msg =
      detailQ.error instanceof ApiError
        ? detailQ.error.message
        : "Couldn't load this score.";
    return (
      <section className="mx-auto mt-12 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Off the rails</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-sm text-muted-foreground">{msg}</p>
            <div className="mt-4">
              <Link
                to="/games/$id/scoreboard"
                params={{ id }}
                className={buttonStyles("ghost", "sm")}
              >
                Back to scoreboard
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (!detail) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (audit.isPending) return;
    const parsed = Number.parseInt(total, 10);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    if (!detail) return;
    if (parsed === detail.score.total && !note.trim()) {
      // No-op submit: don't bug the backend with an identical audit.
      return;
    }
    try {
      await audit.mutateAsync({ total: parsed, note: note.trim() || undefined });
      setNote("");
      setSavedAt(Date.now());
    } catch {
      // surfaced via audit.error
    }
  }

  const errorMessage =
    audit.error instanceof ApiError
      ? audit.error.message
      : audit.error
      ? "Couldn't apply that audit. Try again."
      : null;

  const cantSubmit =
    audit.isPending ||
    total.trim() === "" ||
    !Number.isFinite(Number.parseInt(total, 10)) ||
    Number.parseInt(total, 10) < 0;

  return (
    <section className="mx-auto mt-4 max-w-2xl space-y-5 px-1 pb-32 md:pb-6">
      <header className="flex items-center justify-between gap-3 px-1">
        <Link
          to="/games/$id/scoreboard"
          params={{ id }}
          className={buttonStyles("ghost", "sm")}
        >
          ← Scoreboard
        </Link>
        <Link
          to="/games/$id/round/$n"
          params={{ id, n: String(detail.score.roundNumber) }}
          className={buttonStyles("ghost", "sm")}
        >
          Stop {detail.score.roundNumber}
        </Link>
      </header>

      <DetailHeader detail={detail} gameId={id} scoreId={scoreId} />

      <CurrentScore detail={detail} />

      {detail.score.hasPhoto && (
        <PhotoReinference
          gameId={id}
          scoreId={scoreId}
          currentTotal={detail.score.total}
        />
      )}

      <AuditHistory audits={detail.audits} />

      <Card>
        <CardHeader>
          <CardTitle>Apply an audit</CardTitle>
          <p className="font-mono text-xs text-muted-foreground">
            A recount lands here as a new line in the lineage above.
          </p>
        </CardHeader>
        <CardContent>
          <form id="audit-form" onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-1">
              <Label htmlFor="audit-total">New total</Label>
              <Input
                id="audit-total"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                required
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                className="h-14 text-3xl font-display"
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="audit-note">Note (optional)</Label>
              <Input
                id="audit-note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="recount tally"
                maxLength={140}
              />
              <p className="font-mono text-[11px] text-muted-foreground">
                Up to 140 characters. Shows up in the lineage.
              </p>
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive"
              >
                {errorMessage}
              </div>
            )}

            {savedAt && (
              <div
                role="status"
                className="rounded-md border border-saguaro-700/40 bg-saguaro-700/10 px-3 py-2 font-mono text-sm text-saguaro-700"
              >
                Saved.
              </div>
            )}
          </form>
        </CardContent>

        {/* Desktop CTA */}
        <div className="mt-5 hidden md:flex md:justify-end">
          <Button
            type="submit"
            form="audit-form"
            size="lg"
            disabled={cantSubmit}
          >
            {audit.isPending ? "Auditing…" : "Apply audit"}
          </Button>
        </div>
      </Card>

      {/* Mobile pinned-bottom CTA */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border/50 bg-sand-50/95 px-4 py-3 backdrop-blur md:hidden">
        <Button
          type="submit"
          form="audit-form"
          size="lg"
          disabled={cantSubmit}
          className="w-full"
        >
          {audit.isPending ? "Auditing…" : "Apply audit"}
        </Button>
      </div>
    </section>
  );
}

function DetailHeader({
  detail,
  gameId,
  scoreId,
}: {
  detail: ScoreDetail;
  gameId: string;
  scoreId: string;
}) {
  const [photoBroken, setPhotoBroken] = useState(false);
  return (
    <Card>
      <div className="flex items-start gap-4">
        {detail.score.hasPhoto && !photoBroken && (
          <img
            src={`/api/v1/games/${gameId}/scores/${scoreId}/photo`}
            alt={`${detail.player.name}'s score photo`}
            width={88}
            height={88}
            onError={() => setPhotoBroken(true)}
            className="h-22 w-22 flex-shrink-0 rounded-md border border-rust-500/40 object-cover"
            style={{ width: 88, height: 88 }}
          />
        )}
        <div className="flex-1">
          <div className="font-display text-2xl text-locomotive-900">
            Stop {detail.score.roundNumber}
            <span className="ml-2 font-mono text-sm text-muted-foreground">
              · Engine: double-{detail.score.engineDouble}
            </span>
          </div>
          <div className="mt-1 font-display text-lg text-foreground">
            {detail.player.name}
            {detail.player.isHost && (
              <span className="ml-2 font-mono text-xs text-ochre-600">
                🎩 host
              </span>
            )}
          </div>
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">
            {detail.score.confirmedAt
              ? `confirmed ${new Date(detail.score.confirmedAt).toLocaleString()}`
              : "not yet confirmed"}
          </div>
        </div>
      </div>
    </Card>
  );
}

function CurrentScore({ detail }: { detail: ScoreDetail }) {
  const oldest = detail.audits[detail.audits.length - 1] ?? null;
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            current total
          </div>
          <div className="font-display text-5xl text-foreground">
            {detail.score.total}
          </div>
        </div>
        {oldest && (
          <div className="text-right">
            <div className="font-mono text-[11px] text-muted-foreground">
              audited since
            </div>
            <div className="font-mono text-sm text-foreground">
              {new Date(oldest.createdAt).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function AuditHistory({ audits }: { audits: ScoreAudit[] }) {
  if (audits.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Lineage</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-mono text-sm text-muted-foreground">
            No audits. The score stands as submitted.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Newest first — backend sorts ascending, so reverse.
  const sorted = [...audits].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <section>
      <h2 className="mb-3 px-1 font-display text-2xl">Lineage</h2>
      <ul className="space-y-2">
        {sorted.map((a) => (
          <li key={a.id}>
            <Card className="py-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex-1">
                  <div className="font-mono text-sm text-foreground">
                    <span className="font-display text-base">
                      {a.auditorName}
                    </span>{" "}
                    changed{" "}
                    <span className="text-muted-foreground line-through">
                      {a.oldTotal}
                    </span>{" "}
                    →{" "}
                    <span className="text-rust-600">{a.newTotal}</span>
                  </div>
                  {a.note && (
                    <div className="mt-1 font-mono text-xs text-muted-foreground">
                      note: <span className="text-foreground">"{a.note}"</span>
                    </div>
                  )}
                </div>
                <div className="font-mono text-[11px] text-muted-foreground">
                  {new Date(a.createdAt).toLocaleTimeString()}
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface PhotoReinferenceProps {
  gameId: string;
  scoreId: string;
  currentTotal: number;
}

function PhotoReinference({
  gameId,
  scoreId,
  currentTotal,
}: PhotoReinferenceProps) {
  const photoUrl = `/api/v1/games/${gameId}/scores/${scoreId}/photo`;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ total: number; tiles: number } | null>(
    null,
  );
  const [err, setErr] = useState<string | null>(null);

  async function onRerun() {
    if (loading) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    const worker = createYoloWorker();
    try {
      await worker.ready;
      const r = await fetch(photoUrl, { credentials: "include" });
      if (!r.ok) throw new Error(`photo fetch ${r.status}`);
      const blob = await r.blob();
      const bitmap = await createImageBitmap(blob);
      const res = await worker.image(bitmap);
      // The worker closed the bitmap; we just count counted boxes here.
      const tiles = res.boxes.filter((b) => b.score >= 0.55).length;
      setResult({ total: res.total, tiles });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't run the model.");
    } finally {
      worker.terminate();
      setLoading(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Photo</CardTitle>
        <p className="font-mono text-xs text-muted-foreground">
          Re-run the model against the saved frame. Won't change the score —
          shows you what the model sees today so you can decide whether to
          apply an audit.
        </p>
      </CardHeader>
      <CardContent>
        <img
          src={photoUrl}
          alt="captured tiles"
          className="w-full max-w-md rounded-md border border-rust-500/40 object-contain"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onRerun}
            disabled={loading}
          >
            {loading ? "Running…" : "🔁 Re-run model"}
          </Button>
          {result && (
            <span className="font-mono text-sm text-foreground">
              Model now sees:{" "}
              <span className="font-display text-rust-600">{result.total}</span>{" "}
              <span className="text-muted-foreground">
                ({result.tiles} {result.tiles === 1 ? "tile" : "tiles"} · was{" "}
                {currentTotal})
              </span>
            </span>
          )}
          {err && (
            <span
              role="alert"
              className="font-mono text-sm text-destructive"
            >
              {err}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
